import { useRestaurantStore } from '../../stores/appRestaurantStore';
import { isCombinedOrderingAccount } from '../../utils/spendSideUtils';
import { getAccessToken } from '../authentication/supabaseService';

export interface BackendRestaurantSectionPlatform {
  id: string;
  restaurant_section_id: string;
  platform_name: string;
  enabled: boolean;
  order_emails: string[] | null;
  order_client_names: string[] | null;
  login_username: string | null;
  login_password: string | null;
  created_at: string;
  updated_at: string;
  /**
   * `restaurant_platforms.settings` jsonb — ships on this payload because the routes select `rp.*`.
   * Only `combinedOrderingAccount` is read here (the food/drinks split needs it on every client
   * page); every other key is super-admin ingestion behaviour and must stay unread and unwritten
   * from the client. The only write path is the super-admin PUT in adminSetupService.
   */
  settings?: Record<string, unknown> | null;
}

export interface FrontendRestaurantSectionPlatform {
  id: string;
  restaurantSectionId: string;
  platformName: string;
  enabled: boolean;
  orderEmails: string[] | null;
  orderClientNames: string[] | null;
  loginUsername: string | null;
  loginPassword: string | null;
  createdAt: string;
  updatedAt: string;
  /** Derived from `settings`, read-only for clients. Spec §5. */
  combinedOrderingAccount: boolean;
}

function mapBackendToFrontend(r: BackendRestaurantSectionPlatform): FrontendRestaurantSectionPlatform {
  return {
    id:               r.id,
    restaurantSectionId:     r.restaurant_section_id,
    platformName:     r.platform_name,
    enabled:          r.enabled,
    orderEmails:      r.order_emails,
    orderClientNames:  r.order_client_names,
    loginUsername:    r.login_username,
    loginPassword:    r.login_password,
    createdAt:        r.created_at,
    updatedAt:        r.updated_at,
    combinedOrderingAccount: isCombinedOrderingAccount(r.settings),
  };
}

export async function createRestaurantPlatform(data: {
  restaurantSectionId: string;
  platformName: string;
  enabled: boolean;
  orderEmails?: string[];
  orderClientNames?: string[];
  loginUsername?: string;
  loginPassword?: string;
}): Promise<FrontendRestaurantSectionPlatform> {
  const token = await getAccessToken();
  const res = await fetch('/api/restaurantPlatforms/create', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Failed to create platform entry: ${res.status} ${res.statusText}`);
  }
  const backend: BackendRestaurantSectionPlatform = await res.json();
  return mapBackendToFrontend(backend);
}

export async function fetchRestaurantSectionPlatforms(
  restaurantSectionId: string
): Promise<FrontendRestaurantSectionPlatform[]> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurantPlatforms/getAll/${restaurantSectionId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch platform entries: ${res.status} ${res.statusText}`);
  }
  const list: BackendRestaurantSectionPlatform[] = await res.json();
  return list.map(mapBackendToFrontend);
}

export async function fetchPlatformsBySectionIds(
  sectionIds: string[]
): Promise<FrontendRestaurantSectionPlatform[]> {
  const token = await getAccessToken();
  const res = await fetch('/api/restaurantPlatforms/batch', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ sectionIds }),
  });
  if (!res.ok) {
    throw new Error(`Failed to fetch platform entries: ${res.status} ${res.statusText}`);
  }
  const list: BackendRestaurantSectionPlatform[] = await res.json();
  return list.map(mapBackendToFrontend);
}

export async function updateRestaurantPlatform(
  id: string,
  data: {
    platformName: string;
    enabled: boolean;
    orderEmails?: string[];
    orderClientNames?: string[];
    loginUsername?: string;
    loginPassword?: string;
  }
): Promise<FrontendRestaurantSectionPlatform> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurantPlatforms/${id}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Failed to update platform entry: ${res.status} ${res.statusText}`);
  }
  const backend: BackendRestaurantSectionPlatform = await res.json();
  return mapBackendToFrontend(backend);
}

export async function deleteRestaurantPlatform(id: string): Promise<void> {
  const token = await getAccessToken();
  const res = await fetch(`/api/restaurantPlatforms/delete/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) {
    throw new Error(`Failed to delete platform entry: ${res.status} ${res.statusText}`);
  }
}

export function getAvailablePlatforms(
  restaurantSectionId: string
): FrontendRestaurantSectionPlatform[] {
  const { restaurantSectionPlatforms } = useRestaurantStore.getState();
  const list = restaurantSectionPlatforms[restaurantSectionId] || [];
  return list.filter(
    p =>
      p.enabled &&
      Boolean(p.loginUsername?.trim()) &&
      Boolean(p.loginPassword?.trim())
  );
}
